/**
 * LP: Bracelet Black — Men Self-Purchase
 *
 * - Fade-up intersection observer
 * - Hero gallery (main image + thumbs + arrows)
 * - Hero details accordion
 * - Hero UGC strip → opens UGC lightbox
 * - Permission video controls (mute/play, autoplay-on-visible)
 * - How-It-Works tabs (with attention-wiggle on Step 2)
 * - Reviews carousel (dots ↔ scroll sync)
 * - FAQ accordion
 * - Announce bar + nav hide-on-scroll-down
 * - Sticky CTA appears once user scrolls past hero ATC
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Slide's left position INSIDE the track's scrollable content.
     `slide.offsetLeft` is relative to the slide's offsetParent (the nearest
     positioned ancestor), which may be the wrap/section, NOT the track — that
     causes a wrong snap target whenever the wrap has padding. Subtracting the
     track's own offsetLeft (under the same offsetParent) makes the result
     consistently track-relative. */
  function slideLeftIn(track, slide) {
    return slide.offsetLeft - track.offsetLeft;
  }

  /* Element smooth scroll — `el.scrollTo({behavior:'smooth'})` silently no-ops in
     some embedded browser modes (same issue smoothScrollTo handles for window).
     setInterval-driven animation works everywhere. */
  function smoothScrollElementTo(el, targetLeft, duration) {
    duration = duration == null ? 380 : duration;
    targetLeft = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, targetLeft));
    var startLeft = el.scrollLeft;
    if (Math.abs(targetLeft - startLeft) < 2) return;
    if (prefersReducedMotion || duration <= 0) {
      // Bypass CSS smooth scroll-behavior so the set is instant.
      var prev = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = targetLeft;
      el.style.scrollBehavior = prev;
      return;
    }
    var prev2 = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    var startTime = Date.now();
    function ease(t) { return t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t; }
    var iv = setInterval(function () {
      var elapsed = Date.now() - startTime;
      var p = Math.min(elapsed / duration, 1);
      el.scrollLeft = startLeft + (targetLeft - startLeft) * ease(p);
      if (p >= 1) {
        clearInterval(iv);
        el.style.scrollBehavior = prev2;
      }
    }, 16);
  }

  /* ---------- Smooth scroll polyfill ----------
   * Native window.scrollTo({behavior:'smooth'}) silently no-ops in some embedded
   * browser modes (small viewports, devtools-driven viewports). We try native
   * first, then fall back to setInterval-based animation which works everywhere
   * including throttled-rAF environments.
   */
  function smoothScrollTo(targetY, duration) {
    duration = duration == null ? 500 : duration;
    targetY = Math.max(0, targetY);
    var startY = window.scrollY || window.pageYOffset || 0;
    if (Math.abs(targetY - startY) < 2) return;
    if (prefersReducedMotion || duration <= 0) {
      window.scrollTo(0, targetY);
      return;
    }
    var startTime = Date.now();
    function ease(t) { return t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t; } // easeInOutQuad
    var interval = setInterval(function () {
      var elapsed = Date.now() - startTime;
      var p = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + (targetY - startY) * ease(p));
      if (p >= 1) clearInterval(interval);
    }, 16);
  }

  function initAll(root) {
    root = root || document;
    // Same JS drives every product LP variant — they all wrap their content
    // in a class-scoped div but every interactive hook inside uses data-lp-*
    // attributes, so the controllers are wrapper-agnostic.
    root.querySelectorAll('.lp-men, .lp-wife, .lp-wallet').forEach(initSection);
  }

  function initSection(section) {
    if (section.__lpMenInited) return;
    section.__lpMenInited = true;

    normalizeEmojiWarnings(section);
    initFadeUp(section);
    initGallery(section);
    initPendantSelector(section);
    initHeroDetails(section);
    initUgcLightbox(section);
    initPermissionVideo(section);
    initHowItWorks(section);
    initReviewsCarousel(section);
    initFaq(section);
    initAnnounceNav(section);
    initStickyCta(section);
    initScrollToPersonalize(section);
    // Drawer is now its own component — see lp-drawer.js / snippets/lp-drawer.liquid.
    // It auto-inits on DOMContentLoaded and exposes drawer.__lp = { open, close, setView }
    // which initAddToCart consumes after a successful cart/add.js POST.
    initJp2dPreviewButton(section);
    initImageLightbox(section);
    initAddToCart(section);
  }

  /* ---------- Custom AJAX add-to-cart ----------
   * Intercepts the form submit (+ any [form="..."] external submit buttons),
   * POSTs to /cart/add.js, shows success/error inline.
   */
  function initAddToCart(section) {
    var buy = section.querySelector('[data-lp-buy]');
    if (!buy) return;
    var form = buy.querySelector('form');
    if (!form) return;
    var errorEl = buy.querySelector('[data-lp-buy-error]');
    // Success lives in the right-side cart drawer (mode="cart" or the
    // legacy dual-mode drawer that hosts both nav + cart views).
    // Drawer exposes its controller via `drawer.__lp` (see lp-drawer.js).
    var drawer = section.querySelector('[data-lp-drawer-mode="cart"]')
      || section.querySelector('[data-lp-drawer-mode="dual"]')
      || section.querySelector('[data-lp-drawer]');

    function getAllSubmitButtons() {
      var inside = Array.prototype.slice.call(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
      var outside = Array.prototype.slice.call(document.querySelectorAll('[form="' + form.id + '"][type="submit"]'));
      return inside.concat(outside);
    }

    function setLoading(btn, loading) {
      getAllSubmitButtons().forEach(function (b) {
        if (loading) {
          b.classList.add('is-loading');
          b.setAttribute('disabled', 'disabled');
          b.setAttribute('aria-busy', 'true');
        } else {
          b.classList.remove('is-loading');
          b.removeAttribute('disabled');
          b.removeAttribute('aria-busy');
        }
      });
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || 'Something went wrong. Please try again.';
      errorEl.hidden = false;
    }
    function hideError() { if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; } }

    function showSuccess(_item) {
      // Just open the drawer in cart-success view. Item title sub-text was
      // removed from the drawer markup — the green check + "Added to cart"
      // header carry the message; the upsell + Go to Cart / Checkout
      // buttons fill the rest of the panel.
      // (`_item` kept in the signature so the cart/add.js callback can
      // pass the response payload through; future expansions may use it.)
      if (drawer && drawer.__lp) {
        drawer.__lp.setView('cart');
        drawer.__lp.open();
      }

      // Hide the sticky-mobile CTA so it doesn't sit on top of the drawer.
      var sticky = section.querySelector('[data-lp-sticky-cta]');
      if (sticky) sticky.classList.remove('visible');

      // Refresh cart count badges / dispatch a global event theme-wide components listen to
      fetch('/cart.json').then(function (r) { return r.json(); }).then(function (cart) {
        if (!cart) return;
        document.querySelectorAll('.cart-count').forEach(function (el) {
          el.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      }).catch(function () {});
    }

    function parseError(data) {
      if (!data) return null;
      if (data.description) return data.description;
      if (data.message) return data.message;
      return null;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      hideError();

      if (!form.checkValidity()) { form.reportValidity(); return; }

      var btn = form.querySelector('button[type="submit"]');
      setLoading(btn, true);

      var formData = new FormData(form);
      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, status: res.status, data: data }; });
        })
        .then(function (r) {
          if (!r.ok) {
            showError(parseError(r.data) || 'Could not add to cart (' + r.status + ').');
            return;
          }
          if (buy.hasAttribute('data-lp-direct-checkout')) {
            window.location.href = '/checkout';
            return;
          }
          showSuccess(r.data);
        })
        .catch(function () {
          showError('Network error. Please try again.');
        })
        .finally(function () {
          setLoading(null, false);
        });
    });

    // External submit buttons (sticky CTA, etc.) are wired via the form="..." attribute
    // and fire the form's submit naturally — our submit listener above handles it.
    //
    // No explicit "continue shopping" reset is needed any more — the success
    // confirmation lives in the drawer; closing the drawer drops the user
    // back on the still-populated form, ready to add another bracelet.
  }

  /* ---------- Image lightbox: click any gallery slide to open full-screen ---------- */
  function initImageLightbox(section) {
    var lightbox = section.querySelector('[data-lp-image-lightbox]');
    var imgEl = lightbox && lightbox.querySelector('[data-lp-image-lightbox-img]');
    var closeBtn = lightbox && lightbox.querySelector('[data-lp-image-lightbox-close]');
    var prevBtn = lightbox && lightbox.querySelector('[data-lp-image-lightbox-prev]');
    var nextBtn = lightbox && lightbox.querySelector('[data-lp-image-lightbox-next]');
    var counter = lightbox && lightbox.querySelector('[data-lp-image-lightbox-counter]');
    var slides = section.querySelectorAll('[data-lp-gallery-slide]');
    if (!lightbox || !imgEl || !slides.length) return;

    var currentIdx = 0;
    var slideArr = Array.prototype.slice.call(slides);

    function getSlideImg(slide) { return slide && slide.querySelector('img'); }

    function show(i) {
      currentIdx = Math.max(0, Math.min(slideArr.length - 1, i));
      var slide = slideArr[currentIdx];
      var img = getSlideImg(slide);
      if (!img) return;
      imgEl.src = img.src;
      imgEl.alt = img.alt || '';
      counter.textContent = (currentIdx + 1) + ' / ' + slideArr.length;
      prevBtn.disabled = currentIdx === 0;
      nextBtn.disabled = currentIdx === slideArr.length - 1;
    }

    function open(i) {
      show(i);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      closeBtn.focus({ preventScroll: true });
    }

    function close() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      imgEl.removeAttribute('src');
      document.body.style.overflow = '';
    }

    /* Tap detection: use the browser's native `click` (it already filters out
     * swipes/scrolls on touch devices) — our previous pointerdown/up flow lost
     * the up event when the gesture exited the slide on touch. We additionally
     * suppress click after a horizontal swipe in case the platform fires it. */
    var pointerDownX = 0, pointerDownY = 0, suppressClick = false;
    var SWIPE_THRESHOLD_PX = 10;

    slideArr.forEach(function (slide, idx) {
      slide.addEventListener('pointerdown', function (e) {
        pointerDownX = e.clientX; pointerDownY = e.clientY;
        suppressClick = false;
      }, { passive: true });
      slide.addEventListener('pointermove', function (e) {
        if (Math.abs(e.clientX - pointerDownX) > SWIPE_THRESHOLD_PX ||
            Math.abs(e.clientY - pointerDownY) > SWIPE_THRESHOLD_PX) {
          suppressClick = true;
        }
      }, { passive: true });
      slide.addEventListener('click', function () {
        if (suppressClick) { suppressClick = false; return; }
        open(idx);
      });
    });

    /* Re-sync the preview slide src each time we show it (it's a live data URL) */
    function showRefreshing(i) {
      // Re-query the slide's <img> src in case JP2D updated it after pointerdown
      var slide = slideArr[i];
      var img = getSlideImg(slide);
      if (img) imgEl.src = img.src;
    }

    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { show(currentIdx - 1); showRefreshing(currentIdx); });
    nextBtn.addEventListener('click', function () { show(currentIdx + 1); showRefreshing(currentIdx); });
    imgEl.addEventListener('click', close);
    lightbox.addEventListener('click', function (e) {
      // Click on the dark area outside img/buttons closes
      if (e.target === lightbox || e.target.classList.contains('lp-image-lightbox__figure')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') { show(currentIdx + 1); showRefreshing(currentIdx); }
      else if (e.key === 'ArrowLeft')  { show(currentIdx - 1); showRefreshing(currentIdx); }
    });
  }

  /* ---------- JP2D preview button → scroll OUR gallery to the preview slide.
   * The shared 2d_jewelry_preview snippet's click handler hunts for `media-gallery`
   * + Shopify's swiper. We intercept first (capture phase) and scroll our own track. */
  function initJp2dPreviewButton(section) {
    var track = section.querySelector('[data-lp-gallery-track]');
    var previewSlide = section.querySelector('[data-lp-preview-slide]');
    if (!track || !previewSlide) return;

    section.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-jp2d-scroll]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      // Scroll the page to the gallery
      var galleryRect = section.querySelector('.gallery').getBoundingClientRect();
      var offset = (window.innerWidth >= 768 ? 36 : 34) + 56 + 24;
      smoothScrollTo(window.scrollY + galleryRect.top - offset);

      // Scroll the gallery track to the preview slide
      smoothScrollElementTo(track, slideLeftIn(track, previewSlide));
    }, true);
  }

  /* Nav drawer is its own component now — see snippets/lp-drawer.liquid +
   * assets/lp-drawer.{css,js}. It auto-inits on DOMContentLoaded and pins
   * `drawer.__lp = { open, close, setView }` on each instance, which the
   * AJAX add-to-cart flow above consumes after a successful POST. */

  /* ---------- Mid-page / final / sticky CTAs scroll up to personalization ----------
   * Desktop (>=1024px): two-column hero, so just scroll to the top.
   * Mobile / tablet: scroll to the personalization panel with the actual
   *   measured fixed-header height subtracted so it lands just below the nav.
   */
  function initScrollToPersonalize(section) {
    var triggers = section.querySelectorAll('[data-lp-scroll-to-personalize]');
    if (!triggers.length) return;
    var explicitTarget = section.querySelector('[data-lp-personalization-target]');
    var target = explicitTarget || section.querySelector('.personalization') || section.querySelector('[data-lp-pendant-selector]');
    var announceBar = section.querySelector('.announce-bar');
    var nav = section.querySelector('.lp-nav') || section.querySelector('.fd-nav');

    function fixedHeaderHeight() {
      var h = 0;
      if (announceBar && !announceBar.classList.contains('is-hidden')) {
        h += announceBar.getBoundingClientRect().height;
      }
      if (nav && !nav.classList.contains('is-hidden')) {
        h += nav.getBoundingClientRect().height;
      }
      // Even if both are momentarily hidden, the scroll-up will reveal them, so
      // budget for them anyway.
      if (h === 0 && nav) h = nav.getBoundingClientRect().height + (announceBar ? announceBar.getBoundingClientRect().height : 0);
      return h;
    }

    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if ((window.innerWidth >= 1024 && !explicitTarget) || !target) {
          smoothScrollTo(0);
          return;
        }
        var rect = target.getBoundingClientRect();
        var offset = fixedHeaderHeight() + 16; // 16px breathing room
        smoothScrollTo(window.scrollY + rect.top - offset);
      });
    });
  }

  /* ---------- Emoji-warning normalize ----------
   * The shared custom_variant_field snippet renders the warning with class="hidden ..."
   * but its JS only toggles `tw-hidden`. Outside Tailwind v3 with no prefix, `hidden`
   * is unstyled, so the warning is visible by default. We rename the initial class so
   * the existing toggling logic works.
   */
  function normalizeEmojiWarnings(section) {
    section.querySelectorAll('[id^="emoji-warning-"]').forEach(function (el) {
      if (el.classList.contains('hidden')) {
        el.classList.remove('hidden');
        el.classList.add('tw-hidden');
      }
    });
    var observer = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        if (m.type === 'childList') {
          m.addedNodes.forEach(function (n) {
            if (n.nodeType !== 1) return;
            if (n.id && n.id.indexOf('emoji-warning-') === 0 && n.classList.contains('hidden')) {
              n.classList.remove('hidden');
              n.classList.add('tw-hidden');
            }
            n.querySelectorAll && n.querySelectorAll('[id^="emoji-warning-"].hidden').forEach(function (e) {
              e.classList.remove('hidden');
              e.classList.add('tw-hidden');
            });
          });
        }
      });
    });
    observer.observe(section, { childList: true, subtree: true });
  }

  /* ---------- Pendant-style variant selector(s) ----------
   *
   * Renders one selector per product option (e.g. "Number of names",
   * "Message"). Each `.pendant-btn` carries `data-lp-option-value` and
   * lives inside a wrapper with `data-lp-pendant-selector`.
   *
   * Click flow: toggle `active` within that selector, then look up the
   * variant whose `option1`/`option2`/… matches the currently-active
   * value of every selector (selectors render in option-index order, so
   * selectors[i] → option(i+1)). Single-option products (the bracelet
   * pages) just have one selector and the math collapses to "find the
   * variant whose option1 matches the clicked button".
   */
  function initPendantSelector(section) {
    var selectors = section.querySelectorAll('[data-lp-pendant-selector]');
    if (!selectors.length) return;

    // Prefer the buy-block-scoped form (bracelet pages wrap it in
    // `[data-lp-buy]` to disambiguate from any other form). Fall back to
    // the first <form> in the section so simpler layouts (the wallet
    // upsell page) don't have to introduce that wrapper.
    var form = section.querySelector('[data-lp-buy] form') || section.querySelector('form');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;

    var variantsScript = section.querySelector('[data-lp-variants]');
    var variants = [];
    if (variantsScript) {
      try { variants = JSON.parse(variantsScript.textContent); } catch (e) {}
    }
    if (!variants.length) return;
    var variantsById = {};
    variants.forEach(function (v) { variantsById[String(v.id)] = v; });

    var heroPriceEl = section.querySelector('[data-lp-price]');
    var stickyPriceEl = section.querySelector('[data-lp-sticky-price]');
    var compareEl = section.querySelector('.price-compare');

    // Map each selector's currently-active value into the corresponding
    // option key (option1, option2, …) so we can match against `variants`.
    function findVariantForCurrentSelection() {
      for (var vi = 0; vi < variants.length; vi++) {
        var v = variants[vi];
        var match = true;
        for (var si = 0; si < selectors.length; si++) {
          var activeBtn = selectors[si].querySelector('.pendant-btn.active');
          if (!activeBtn) { match = false; break; }
          var key = 'option' + (si + 1);
          if (v[key] !== activeBtn.getAttribute('data-lp-option-value')) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    function applyVariant(id, opts) {
      opts = opts || {};
      var v = variantsById[String(id)];
      if (!v) return;

      // Hidden form input — set both property AND attribute so 2D-preview's
      // MutationObserver (watches attributeFilter: ['value']) picks it up.
      idInput.value = v.id;
      idInput.setAttribute('value', v.id);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variant: { id: Number(v.id) } }
      }));

      if (heroPriceEl) heroPriceEl.textContent = v.price_formatted;
      if (stickyPriceEl) stickyPriceEl.textContent = v.price_formatted;

      var hasCompare = v.compare_at_price && v.compare_at_price > v.price;
      if (compareEl) {
        if (hasCompare) {
          compareEl.textContent = v.compare_formatted;
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      } else if (hasCompare && heroPriceEl) {
        var sp = document.createElement('span');
        sp.className = 'price-compare';
        sp.textContent = v.compare_formatted;
        heroPriceEl.parentNode.appendChild(sp);
      }

      section.querySelectorAll('button[type="submit"]').forEach(function (b) {
        b.disabled = !v.available;
      });

      if (!opts.skipUrl && window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }

      section.setAttribute('data-variant-id', v.id);
    }

    selectors.forEach(function (sel) {
      var btns = sel.querySelectorAll('.pendant-btn');
      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          btns.forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          var matched = findVariantForCurrentSelection();
          if (matched) applyVariant(matched.id);
        });
      });
    });

    // Sync to URL ?variant= on initial load — set every selector's active
    // button to the values that match the URL variant, then apply.
    try {
      var u = new URL(window.location.href);
      var qv = u.searchParams.get('variant');
      if (qv && variantsById[qv]) {
        var v = variantsById[qv];
        selectors.forEach(function (sel, idx) {
          var key = 'option' + (idx + 1);
          var val = v[key];
          if (val == null) return;
          sel.querySelectorAll('.pendant-btn').forEach(function (b) {
            b.classList.toggle('active', b.getAttribute('data-lp-option-value') === val);
          });
        });
        applyVariant(qv, { skipUrl: true });
      }
    } catch (e) {}
  }

  /* ---------- Fade-up ---------- */
  function initFadeUp(section) {
    var targets = section.querySelectorAll('.fade-up');
    if (!targets.length) return;
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Gallery (swipe-able main + thumbs nav) ---------- */
  function initGallery(section) {
    var track = section.querySelector('[data-lp-gallery-track]');
    var slides = track ? track.querySelectorAll('[data-lp-gallery-slide]') : [];
    var thumbs = section.querySelectorAll('[data-lp-gallery-thumb]');
    var thumbsScroll = section.querySelector('[data-lp-gallery-thumbs]');
    var thumbArrowLeft = section.querySelector('[data-lp-gallery-prev]');
    var thumbArrowRight = section.querySelector('[data-lp-gallery-next]');
    var mainPrev = section.querySelector('[data-lp-gallery-main-prev]');
    var mainNext = section.querySelector('[data-lp-gallery-main-next]');
    // Gallery wrapper — toggle `is-on-preview` on it whenever the
    // currently-visible slide is the JP2D preview slide, so the disclaimer
    // (rendered as a sibling underneath) only shows for that slide.
    var galleryWrap = section.querySelector('.gallery');

    /* --- Main swipe / scroll-snap track --- */
    if (track && slides.length) {
      var currentIdx = 0;
      function scrollToIdx(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        var slide = slides[i];
        if (slide) smoothScrollElementTo(track, slideLeftIn(track, slide));
      }
      function syncPreviewClass(activeSlide) {
        if (!galleryWrap || !activeSlide) return;
        var isPreview = activeSlide.hasAttribute('data-lp-preview-slide');
        galleryWrap.classList.toggle('is-on-preview', isPreview);
      }
      // Initial sync — first slide may be the preview slide on the wallet
      // page (single-slide gallery).
      syncPreviewClass(slides[0]);
      function updateActive() {
        var center = track.scrollLeft + track.clientWidth / 2;
        var closest = 0;
        var min = Infinity;
        slides.forEach(function (s, i) {
          var c = slideLeftIn(track, s) + s.offsetWidth / 2;
          var d = Math.abs(c - center);
          if (d < min) { min = d; closest = i; }
        });
        if (closest !== currentIdx) {
          currentIdx = closest;
          thumbs.forEach(function (t, i) { t.classList.toggle('active', i === closest); });
          if (mainPrev) mainPrev.disabled = closest === 0;
          if (mainNext) mainNext.disabled = closest === slides.length - 1;
          syncPreviewClass(slides[closest]);
        }
      }
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (!ticking) {
          window.requestAnimationFrame(function () { updateActive(); ticking = false; });
          ticking = true;
        }
      }, { passive: true });
      window.addEventListener('resize', updateActive);

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var idx = parseInt(thumb.getAttribute('data-index') || '0', 10);
          scrollToIdx(idx);
        });
      });
      if (mainPrev) mainPrev.addEventListener('click', function () { scrollToIdx(currentIdx - 1); });
      if (mainNext) mainNext.addEventListener('click', function () { scrollToIdx(currentIdx + 1); });

      if (mainPrev) mainPrev.disabled = true;
      if (mainNext) mainNext.disabled = slides.length <= 1;

      // Clamp each touch swipe to advance at most one slide (prevents iOS
      // momentum from flying past 2-3 images on a single flick).
      clampSwipeToOneSlide(track, slides);
    }

    /* --- Thumb strip horizontal scroll arrows --- */
    if (thumbsScroll && thumbArrowLeft && thumbArrowRight) {
      var scrollStep = 152;
      function updateArrows() {
        var scrollLeft = thumbsScroll.scrollLeft;
        var maxScroll = thumbsScroll.scrollWidth - thumbsScroll.clientWidth;
        thumbArrowLeft.disabled = scrollLeft <= 2;
        thumbArrowRight.disabled = scrollLeft >= maxScroll - 2;
      }
      thumbArrowLeft.addEventListener('click', function () {
        thumbsScroll.scrollBy({ left: -scrollStep, behavior: 'smooth' });
      });
      thumbArrowRight.addEventListener('click', function () {
        thumbsScroll.scrollBy({ left: scrollStep, behavior: 'smooth' });
      });
      thumbsScroll.addEventListener('scroll', updateArrows);
      window.addEventListener('resize', updateArrows);
      updateArrows();
    }
  }

  /* ---------- Hero details accordion ---------- */
  function initHeroDetails(section) {
    section.querySelectorAll('.hero-details__tab').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        var content = btn.nextElementSibling;
        if (content && content.classList.contains('hero-details__content')) {
          content.classList.toggle('is-open', !isOpen);
        }
      });
    });
  }

  /* ---------- UGC lightbox ---------- */
  function initUgcLightbox(section) {
    var lightbox = section.querySelector('[data-lp-lightbox]');
    if (!lightbox) return;
    var player = lightbox.querySelector('video');
    var closeBtn = lightbox.querySelector('[data-lp-lightbox-close]');

    function open(src) {
      if (!src) return;
      player.src = src;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      player.play().catch(function () {});
    }
    function close() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      player.pause();
      player.removeAttribute('src');
      player.load();
      document.body.style.overflow = '';
    }
    section.querySelectorAll('[data-lp-ugc-card]').forEach(function (card) {
      card.addEventListener('click', function () { open(card.getAttribute('data-video')); });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    lightbox.addEventListener('click', function (e) { if (e.target === lightbox) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ---------- Permission video ---------- */
  function initPermissionVideo(section) {
    var wrap = section.querySelector('[data-lp-permission-video]');
    if (!wrap) return;
    var video = wrap.querySelector('video');
    var muteBtn = wrap.querySelector('[data-lp-permission-mute]');
    var iconMuted = wrap.querySelector('[data-lp-permission-icon-muted]');
    var iconSound = wrap.querySelector('[data-lp-permission-icon-sound]');
    var hint = wrap.querySelector('[data-lp-permission-hint]');
    if (!video || !muteBtn) return;

    function setMuted(muted) {
      video.muted = muted;
      if (iconMuted) iconMuted.style.display = muted ? '' : 'none';
      if (iconSound) iconSound.style.display = muted ? 'none' : '';
      muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      wrap.classList.toggle('is-unmuted', !muted);
      // Hint is only relevant while the video is muted — once we have
      // sound (either because autoplay-with-audio worked or the user
      // tapped to unmute) the prompt is just visual noise.
      if (hint) hint.style.opacity = muted ? '' : '0';
    }

    function rewindIfEnded() {
      // Calling play() on an ended video usually auto-rewinds, but Safari
      // is inconsistent — set currentTime explicitly so a "replay" tap
      // always starts from the beginning.
      if (video.ended || (video.duration && video.currentTime >= video.duration - 0.05)) {
        try { video.currentTime = 0; } catch (e) {}
      }
    }

    function unmuteAndPlay() {
      setMuted(false);
      rewindIfEnded();
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        // If unmuted play is rejected (browser autoplay policy), fall back
        // to muted playback so the user still sees the video and can tap
        // the unmute control once they've interacted with the page.
        p.catch(function () { setMuted(true); video.play().catch(function () {}); });
      }
    }

    // Try unmuted-by-default. Browsers usually block autoplay with sound,
    // in which case the .catch() drops us back to muted + the visible hint.
    unmuteAndPlay();

    muteBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      setMuted(!video.muted);
      if (video.paused) video.play().catch(function () {});
    });
    if (hint) {
      hint.addEventListener('click', function (e) {
        e.stopPropagation();
        unmuteAndPlay();
      });
    }
    wrap.addEventListener('click', function (e) {
      if (e.target.closest('.video-ctrl')) return;
      if (e.target.closest('[data-lp-permission-hint]')) return;
      // First click while still muted = "the user wants sound", not
      // "pause the video". Unmute instead of toggling play/pause.
      if (video.muted) { unmuteAndPlay(); return; }
      if (video.paused) { rewindIfEnded(); video.play().catch(function () {}); } else { video.pause(); }
    });
    video.addEventListener('play', function () { wrap.classList.remove('is-paused'); });
    video.addEventListener('pause', function () { wrap.classList.add('is-paused'); });

    // `loop` was dropped from the markup so the video stops at the end.
    // Track that explicitly so the IntersectionObserver below doesn't
    // restart the video when the user scrolls back to it.
    var hasFinished = false;
    video.addEventListener('ended', function () {
      hasFinished = true;
      wrap.classList.add('is-paused');
    });

    var userInteracted = false;
    muteBtn.addEventListener('click', function () { userInteracted = true; });
    if (hint) hint.addEventListener('click', function () { userInteracted = true; });

    if ('IntersectionObserver' in window) {
      var visObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting && !video.paused) {
            video.pause();
          } else if (entry.isIntersecting && video.paused && !userInteracted && !hasFinished) {
            video.play().catch(function () {});
          }
        });
      }, { threshold: 0.5 });
      visObs.observe(video);
    }
    // Fade the hint after 8s if the user hasn't interacted by then.
    setTimeout(function () { if (hint && video.muted) hint.style.opacity = '0'; }, 8000);
  }

  function initTabbedHowItWorks(section) {
    var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-lp-how-tab]'));
    var panes = Array.prototype.slice.call(section.querySelectorAll('[data-lp-how-pane]'));
    if (!tabs.length || !panes.length) return false;

    var tabsContainer = tabs[0].closest('.howitworks-tabs');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var step = tab.getAttribute('data-step');
        tabs.forEach(function (item) {
          var active = item.getAttribute('data-step') === step;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panes.forEach(function (pane) {
          pane.classList.toggle('is-active', pane.getAttribute('data-step') === step);
        });
        if (tabsContainer) tabsContainer.classList.add('is-engaged');
      });
    });

    if (tabsContainer && 'IntersectionObserver' in window && !prefersReducedMotion) {
      var wiggleObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            tabsContainer.classList.add('is-in-view');
            wiggleObserver.disconnect();
          }
        });
      }, { threshold: 0.3 });
      wiggleObserver.observe(tabsContainer);
    }

    return true;
  }

  /* ---------- How It Works (scroll-driven) ----------
   * The stage is ~220-240vh tall at every viewport. The .howitworks-body is
   * sticky-pinned. Scroll position within the stage drives:
   *   - the rail progress line (0 → 1 across all steps)
   *   - which rail node is active / completed
   *   - which numbered card is visible (crossfade — only one ever shown)
   */
  function initHowItWorks(section) {
    if (initTabbedHowItWorks(section)) return;

    var stage = section.querySelector('[data-lp-how-stage]');
    var railFill = section.querySelector('[data-lp-how-rail-fill]');
    var nodes = Array.prototype.slice.call(section.querySelectorAll('.howitworks-node'));
    var cards = Array.prototype.slice.call(section.querySelectorAll('.howitworks-card'));
    if (!stage || !nodes.length || !cards.length) return;
    var stepCount = nodes.length;

    function fixedHeaderH() {
      var ann = section.querySelector('.announce-bar');
      var nav = section.querySelector('.lp-nav');
      var h = 0;
      if (ann) h += ann.getBoundingClientRect().height;
      if (nav) h += nav.getBoundingClientRect().height;
      return h;
    }

    function setState(activeStep, overallProgress) {
      if (railFill) railFill.style.transform = 'scaleX(' + Math.max(0, Math.min(1, overallProgress)) + ')';
      nodes.forEach(function (n, i) {
        var stepIndex = i + 1;
        n.classList.toggle('is-active', stepIndex === activeStep);
        n.classList.toggle('is-complete', stepIndex < activeStep);
      });
      cards.forEach(function (c, i) {
        c.classList.toggle('is-active', (i + 1) === activeStep);
      });
    }

    /* Rail node click → scroll to mid-zone of that step */
    nodes.forEach(function (node, i) {
      node.style.cursor = 'pointer';
      node.addEventListener('click', function () {
        var step = i + 1;
        var rect = stage.getBoundingClientRect();
        var stageTop = window.scrollY + rect.top;
        var travel = stage.offsetHeight - window.innerHeight;
        if (travel <= 0) return;
        var midOfStep = ((step - 0.5) / stepCount) * travel;
        smoothScrollTo(stageTop + midOfStep - fixedHeaderH() - 16);
      });
    });

    function update() {
      var rect = stage.getBoundingClientRect();
      var travel = stage.offsetHeight - window.innerHeight;
      if (travel <= 0) { setState(1, 0); return; }
      var scrolled = -rect.top;
      var p = scrolled / travel;
      if (p <= 0) { setState(1, 0); return; }
      if (p >= 1) { setState(stepCount, 1); return; }
      var stepFloat = p * stepCount;
      var step = Math.min(stepCount, Math.floor(stepFloat) + 1);
      setState(step, p);
    }

    /* Synchronous scroll handler — rAF gets throttled in some embedded modes. */
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Reviews carousel ---------- */
  function initReviewsCarousel(section) {
    var track = section.querySelector('[data-lp-reviews-track]');
    var dotsContainer = section.querySelector('[data-lp-reviews-dots]');
    if (!track || !dotsContainer) return;
    var slides = track.querySelectorAll('.review-slide');
    var dots = dotsContainer.querySelectorAll('.reviews-carousel__dot');
    function setActive(i) {
      dots.forEach(function (d, j) { d.classList.toggle('is-active', i === j); });
    }
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var i = parseInt(dot.dataset.slide, 10);
        if (slides[i]) smoothScrollElementTo(track, slideLeftIn(track, slides[i]));
      });
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
            setActive(parseInt(entry.target.dataset.slide, 10));
          }
        });
      }, { root: track, threshold: [0.55, 0.75] });
      slides.forEach(function (s) { io.observe(s); });
    }

    // Cap swipe distance to 1 review per gesture so fast flicks can't blow past several
    clampSwipeToOneSlide(track, slides);
  }

  /* ---------- Bounded swipe controller ----------
   * Native horizontal scroll is disabled (overflow-x: hidden in CSS). We drive
   * scrollLeft directly from touch events so a single gesture can move at most
   * one slide — no momentum overshoot, no snap-back. Vertical pan stays native
   * (touch-action: pan-y is set on the gallery).
   *
   *   - touchstart: record start position + start slide
   *   - touchmove: scrollLeft = startScroll - dx, clamped to ±1 slide width
   *   - touchend: if |dx| > 25% of slide width OR fast flick → next slide;
   *               else → snap back to start slide
   */
  function clampSwipeToOneSlide(track, slides) {
    if (!track || !slides || !slides.length) return;

    function slideWidth() {
      // Uniform slides: distance between two consecutive slide left edges (incl. gap).
      if (slides.length > 1) return slideLeftIn(track, slides[1]) - slideLeftIn(track, slides[0]);
      return slides[0].offsetWidth || track.clientWidth;
    }

    function nearestIndex() {
      var center = track.scrollLeft + track.clientWidth / 2;
      var closest = 0, min = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var c = slideLeftIn(track, slides[i]) + slides[i].offsetWidth / 2;
        var d = Math.abs(c - center);
        if (d < min) { min = d; closest = i; }
      }
      return closest;
    }

    function snapTo(idx) {
      idx = Math.max(0, Math.min(slides.length - 1, idx));
      var slide = slides[idx];
      if (slide) smoothScrollElementTo(track, slideLeftIn(track, slide));
    }

    var startX = 0, startY = 0;
    var startScroll = 0;
    var startIdx = 0;
    var startTime = 0;
    var active = false;
    var horizontal = null; // null until we decide axis

    function onStart(e) {
      if (!e.touches || e.touches.length !== 1) return;
      active = true;
      horizontal = null;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startScroll = track.scrollLeft;
      startIdx = nearestIndex();
      startTime = Date.now();
      // CSS sets scroll-behavior: smooth on the track for click/dot/arrow nav.
      // During a drag we need scrollLeft = X to apply instantly, otherwise the
      // finger moves but the slides lag behind. Restored on touchend.
      track.style.scrollBehavior = 'auto';
    }

    function onMove(e) {
      if (!active || !e.touches || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;
      // Decide axis once: if user moved more vertically, defer to native scroll
      if (horizontal === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        horizontal = Math.abs(dx) > Math.abs(dy);
        if (!horizontal) { active = false; return; }
      }
      // Clamp the swipe distance to ±1 slide width (with a touch of resistance
      // beyond the bounds so the user feels the wall).
      var sw = slideWidth();
      var raw = -dx;
      var clamped = Math.max(-sw, Math.min(sw, raw));
      // Tiny rubber-band when pushing past
      if (raw > sw)  clamped = sw + (raw - sw) * 0.18;
      if (raw < -sw) clamped = -sw + (raw + sw) * 0.18;
      track.scrollLeft = startScroll + clamped;
    }

    function onEnd(e) {
      if (!active) return;
      active = false;
      // Restore CSS smooth-scroll so the snap animates.
      track.style.scrollBehavior = '';
      if (horizontal === false) return; // never engaged horizontal axis
      var t = (e.changedTouches && e.changedTouches[0]) || null;
      var dx = t ? (t.clientX - startX) : 0;
      var dt = Date.now() - startTime;
      var sw = slideWidth();
      var velocity = Math.abs(dx) / Math.max(dt, 1); // px/ms
      var DIST_THRESHOLD = sw * 0.18;   // 18% of slide width
      var VEL_THRESHOLD = 0.4;          // ~400 px/s
      var target = startIdx;
      if (Math.abs(dx) > DIST_THRESHOLD || velocity > VEL_THRESHOLD) {
        target = startIdx + (dx < 0 ? 1 : -1);
      }
      snapTo(target);
    }

    track.addEventListener('touchstart', onStart, { passive: true });
    track.addEventListener('touchmove', onMove, { passive: true });
    track.addEventListener('touchend', onEnd, { passive: true });
    track.addEventListener('touchcancel', onEnd, { passive: true });
  }

  /* ---------- FAQ ---------- */
  function initFaq(section) {
    section.querySelectorAll('.faq-question').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.parentElement;
        var isOpen = item.classList.contains('open');
        section.querySelectorAll('.faq-item').forEach(function (i) {
          i.classList.remove('open');
          var q = i.querySelector('.faq-question');
          if (q) q.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ---------- Announce bar + nav hide on scroll ---------- */
  function initAnnounceNav(section) {
    var announceBar = section.querySelector('.announce-bar');
    var nav = section.querySelector('.lp-nav');
    if (!nav) return;
    var lastY = window.scrollY;
    var ticking = false;
    var threshold = 40;
    function onScroll() {
      var y = window.scrollY;
      if (y < threshold) {
        if (announceBar) announceBar.classList.remove('is-hidden');
        nav.classList.remove('is-hidden');
      } else if (y > lastY) {
        if (announceBar) announceBar.classList.add('is-hidden');
        nav.classList.add('is-hidden');
      } else if (y < lastY) {
        if (announceBar) announceBar.classList.remove('is-hidden');
        nav.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
  }

  /* ---------- Sticky CTA ---------- */
  function initStickyCta(section) {
    var heroCta = section.querySelector('[data-lp-hero-cta]');
    var stickyCta = section.querySelector('[data-lp-sticky-cta]');
    if (!heroCta || !stickyCta) return;
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var scrolledPast = entry.boundingClientRect.bottom < 0;
        stickyCta.classList.toggle('visible', scrolledPast);
      });
    }, { threshold: 0 }).observe(heroCta);
  }

  /* ---------- Boot ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
})();
